import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Cog, Plus, Search, MoreVertical, Trash2, Eye, RefreshCw, Plane, Edit } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const ENGINE_STATUS = {
  installed: { label: 'Installed', color: 'bg-green-500/20 text-green-400' },
  serviceable: { label: 'Serviceable', color: 'bg-blue-500/20 text-blue-400' },
  unserviceable: { label: 'Unserviceable', color: 'bg-red-500/20 text-red-400' },
  in_shop: { label: 'In Shop', color: 'bg-yellow-500/20 text-yellow-400' },
  lease_pool: { label: 'Lease Pool', color: 'bg-purple-500/20 text-purple-400' },
  retired: { label: 'Retired', color: 'bg-gray-500/20 text-gray-400' },
};

const ENGINE_TYPES = [
  { value: 'turbofan', label: 'Turbofan' },
  { value: 'turboprop', label: 'Turboprop' },
  { value: 'turboshaft', label: 'Turboshaft' },
  { value: 'piston', label: 'Piston' },
  { value: 'turbojet', label: 'Turbojet' },
];

export default function EnginesPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  const [engines, setEngines] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    serial_number: '', engine_type: 'turbofan', manufacturer: '', model: '',
    status: 'serviceable', installed_aircraft_id: '', position: '', tsn: '', csn: '', notes: '',
    owner_id: '', operator_id: ''
  });

  useEffect(() => {
    fetchEngines();
    fetchStats();
    fetchAircraft();
    fetchOrganizations();
  }, [api]);

  const fetchEngines = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/engines${searchQuery ? `?search=${searchQuery}` : ''}`);
      setEngines(response.data);
    } catch (error) {
      toast.error('Failed to fetch engines');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/engines/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchAircraft = async () => {
    try {
      const response = await api.get('/aircraft?limit=200');
      setAircraft(response.data);
    } catch (error) {
      console.error('Failed to fetch aircraft:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations?limit=200');
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      serial_number: '', engine_type: 'turbofan', manufacturer: '', model: '',
      status: 'serviceable', installed_aircraft_id: '', position: '', tsn: '', csn: '', notes: '',
      owner_id: '', operator_id: ''
    });
    setEditMode(false);
    setEditingId(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleCreateEngine = async () => {
    try {
      if (!formData.serial_number || !formData.manufacturer || !formData.model) {
        toast.error('Please fill required fields');
        return;
      }
      const payload = {
        ...formData,
        tsn: formData.tsn ? parseFloat(formData.tsn) : null,
        csn: formData.csn ? parseInt(formData.csn) : null,
      };
      
      if (editMode && editingId) {
        await api.put(`/engines/${editingId}`, payload);
        toast.success('Engine updated');
      } else {
        await api.post('/engines', payload);
        toast.success('Engine registered');
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchEngines();
      fetchStats();
    } catch (error) {
      toast.error(`Failed to ${editMode ? 'update' : 'create'} engine`);
    }
  };

  const handleEditEngine = async (engine) => {
    try {
      const response = await api.get(`/engines/${engine.id}`);
      const data = response.data;
      
      setFormData({
        serial_number: data.serial_number || '',
        engine_type: data.engine_type || 'turbofan',
        manufacturer: data.manufacturer || '',
        model: data.model || '',
        status: data.status || 'serviceable',
        installed_aircraft_id: data.installed_aircraft_id || '',
        position: data.position || '',
        tsn: data.tsn?.toString() || '',
        csn: data.csn?.toString() || '',
        notes: data.notes || '',
        owner_id: data.owner_id || '',
        operator_id: data.operator_id || ''
      });
      
      setEditMode(true);
      setEditingId(engine.id);
      setShowCreateDialog(true);
    } catch (error) {
      toast.error('Failed to load engine details');
    }
  };

  const getOrgsByType = (type) => {
    return organizations.filter(org => org.organization_types?.includes(type));
  };

  const filteredEngines = engines.filter(e => 
    !searchQuery || e.serial_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.model?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>Engines</h1>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Manage engine registry</p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2" data-testid="create-engine-btn">
          <Plus className="w-4 h-4" /> Add Engine
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>{stats.total || 0}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Total Engines</div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-500">{stats.installed || 0}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Installed</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search engines..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <Button variant="outline" onClick={fetchEngines}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? "border-slate-800" : ""}>
                <TableHead className={isDark ? "text-slate-400" : ""}>Serial Number</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Type / Model</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Installed On</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>TSN/CSN</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8"><RefreshCw className="w-4 h-4 animate-spin inline" /> Loading...</TableCell></TableRow>
              ) : filteredEngines.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">No engines found</TableCell></TableRow>
              ) : (
                filteredEngines.map(eng => (
                  <TableRow key={eng.id} className={cn(isDark ? "border-slate-800 hover:bg-slate-800/50" : "hover:bg-gray-50")}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Cog className={cn("w-4 h-4", isDark ? "text-orange-400" : "text-orange-600")} />
                        <div>
                          <div className={cn("font-mono font-bold", isDark ? "text-white" : "")}>{eng.serial_number}</div>
                          <div className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>{eng.engine_number}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={isDark ? "text-white" : ""}>{eng.manufacturer} {eng.model}</div>
                      <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>{eng.engine_type}</div>
                    </TableCell>
                    <TableCell>
                      {eng.installed_aircraft_registration ? (
                        <div className="flex items-center gap-1">
                          <Plane className="w-3 h-3" />
                          <span className="font-mono">{eng.installed_aircraft_registration}</span>
                          {eng.position && <Badge variant="outline" className="text-xs ml-1">#{eng.position}</Badge>}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className={cn("text-xs", isDark ? "text-slate-300" : "")}>
                        {eng.tsn ? `${eng.tsn}h` : '-'} / {eng.csn ? `${eng.csn}c` : '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={ENGINE_STATUS[eng.status]?.color}>{ENGINE_STATUS[eng.status]?.label || eng.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem><Eye className="w-4 h-4 mr-2" />View Details</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditEngine(eng)}><Edit className="w-4 h-4 mr-2" />Edit Engine</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-500"><Trash2 className="w-4 h-4 mr-2" />Retire</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateDialog(open); }}>
        <DialogContent className={cn("max-w-lg", isDark ? "bg-slate-900 border-slate-800" : "")}>
          <DialogHeader><DialogTitle className={isDark ? "text-white" : ""}>{editMode ? 'Edit Engine' : 'Register Engine'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Serial Number *</Label>
                <Input value={formData.serial_number} onChange={(e) => setFormData(p => ({...p, serial_number: e.target.value}))} placeholder="ESN-12345" className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Engine Type</Label>
                <Select value={formData.engine_type} onValueChange={(v) => setFormData(p => ({...p, engine_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ENGINE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Manufacturer *</Label>
                <Select value={formData.manufacturer} onValueChange={(v) => setFormData(p => ({...p, manufacturer: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CFM">CFM International</SelectItem>
                    <SelectItem value="GE">GE Aviation</SelectItem>
                    <SelectItem value="Rolls-Royce">Rolls-Royce</SelectItem>
                    <SelectItem value="Pratt & Whitney">Pratt & Whitney</SelectItem>
                    <SelectItem value="Safran">Safran</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Model *</Label>
                <Input value={formData.model} onChange={(e) => setFormData(p => ({...p, model: e.target.value}))} placeholder="CFM56-5B" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Installed Aircraft</Label>
                <Select value={formData.installed_aircraft_id} onValueChange={(v) => setFormData(p => ({...p, installed_aircraft_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select aircraft" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Not installed</SelectItem>
                    {aircraft.map(ac => <SelectItem key={ac.id} value={ac.id}>{ac.registration}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Position</Label>
                <Input value={formData.position} onChange={(e) => setFormData(p => ({...p, position: e.target.value}))} placeholder="1, 2, L, R" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>TSN (Hours)</Label>
                <Input type="number" value={formData.tsn} onChange={(e) => setFormData(p => ({...p, tsn: e.target.value}))} placeholder="12500" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>CSN (Cycles)</Label>
                <Input type="number" value={formData.csn} onChange={(e) => setFormData(p => ({...p, csn: e.target.value}))} placeholder="8500" />
              </div>
            </div>
            {/* Owner & Operator */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Owner</Label>
                <Select value={formData.owner_id} onValueChange={(v) => setFormData(p => ({...p, owner_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {getOrgsByType('owner').map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                    ))}
                    {getOrgsByType('leasing_company').map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Operator</Label>
                <Select value={formData.operator_id} onValueChange={(v) => setFormData(p => ({...p, operator_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select operator" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {getOrgsByType('operator').map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateEngine}>{editMode ? 'Update Engine' : 'Register Engine'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
